/**
 * La confirmación de deshacer.
 *
 * *** Por qué existe, si el resto de la app no confirma nada. *** Toda la app está armada
 * sobre la idea de que el comerciante puede animarse porque siempre hay vuelta atrás. Acá
 * no la hay: **no existe deshacer del deshacer**. Es el único botón de la app cuyo error
 * no tiene arreglo simple, y en el historial vive al lado de filas que dicen todas
 * "Manual · Completada · 3".
 *
 * *** Muestra lo que va a pasar, no una pregunta genérica. *** "¿Estás seguro?" no aporta
 * nada: el que quiere seguir aprieta sin leer. Con el número de precios, la fecha exacta y
 * la regla adelante, el que se equivocó de fila lo ve antes de que sea tarde, y el que
 * quería hacerlo confirma igual de rápido.
 *
 * Es el mismo modal en el historial y en el detalle. En el detalle el contexto ya es claro
 * y podría no llevar confirmación, pero dos comportamientos distintos para el mismo botón
 * son la clase de inconsistencia que después nadie recuerda por qué existe.
 */

import { Box, Button, Modal, Text } from '@nimbus-ds/components';
import type { ReactElement } from 'react';
import type { Corrida } from '../api';
import { descripcionDeRegla, useNombresDeCategorias } from '../categorias';
import { fechaCortaConHora, numero } from '../formato';

interface Props {
  /** La corrida a deshacer, o `null` con el modal cerrado. */
  readonly corrida: Corrida | null;
  readonly deshaciendo: boolean;
  readonly onCancelar: () => void;
  readonly onConfirmar: () => void;
}

export function ModalDeshacer({
  corrida,
  deshaciendo,
  onCancelar,
  onConfirmar,
}: Props): ReactElement {
  // Los nombres se piden recién cuando el modal se abre sobre una regla con categorías:
  // es un gesto deliberado del comerciante, no el costo de abrir una pantalla.
  const categorias = useNombresDeCategorias(
    corrida !== null && (corrida.regla?.spec?.scope.categoryIds.length ?? 0) > 0,
  );
  const regla = corrida === null ? null : descripcionDeRegla(corrida, categorias);

  return (
    <Modal open={corrida !== null} onDismiss={onCancelar}>
      <Modal.Header title="¿Deshacer esta actualización?" />
      <Modal.Body padding="none">
        {corrida !== null && (
          <Box display="flex" flexDirection="column" gap="2">
            <Text>
              Vas a volver {numero(corrida.totales.actualizadas)} precios a como estaban el{' '}
              {fechaCortaConHora(corrida.creadaEn)}.
            </Text>
            {regla !== null && <Text>Regla: {regla}.</Text>}
            <Text fontSize="caption" color="neutral-textLow">
              Esto no se puede deshacer: para volver a aplicar el cambio hay que crear una
              actualización nueva.
            </Text>
          </Box>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onCancelar}>Cancelar</Button>
        <Button appearance="danger" disabled={deshaciendo} onClick={onConfirmar}>
          {deshaciendo ? 'Deshaciendo…' : 'Deshacer'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
